/**
 * Client submission identifiers for actions that must happen at most once.
 *
 * A kiosk action that creates durable state — an eEB request, an appointment,
 * a prescription task — can be submitted twice by a double tap, a refresh, or
 * a retry after a timeout whose result never arrived. The client mints one id
 * per intent and reuses it across retries; the server records the id and
 * returns the original result instead of acting again.
 *
 * The id lives in `sessionStorage` so it survives a reload of the same patient
 * session and disappears with it. Storage may be denied (private mode, locked
 * down kiosk browser); the id is then held in memory for the page, so a retry
 * still reaches the server under the same id.
 *
 * An id belongs to **one intent**, not to the session: callers pass a
 * fingerprint of what they are submitting, and changing it mints a new id. A
 * kiosk session outlives many patients, so a session-scoped id would let one
 * patient's retry replay another patient's booking.
 *
 * @since 1.1.0
 */

/**
 * Return the submission id for `storageKey` and `intentFingerprint`.
 *
 * The same fingerprint returns the same id (a retry); a different one mints a
 * new id (a different action). Omit the fingerprint for a flow whose storage
 * key already identifies exactly one intent.
 *
 * @since 1.1.0
 * @example
 * ```ts
 * const submissionId = getOrCreateClientSubmissionId(
 *   "frontdesk-booking-submission-id",
 *   `${patientId}|${slotStart}|${slotEnd}`,
 * )
 * ```
 */
export function getOrCreateClientSubmissionId(
  storageKey: string,
  intentFingerprint = "",
): string {
  const stored = readStoredSubmission(storageKey);
  if (stored !== undefined && stored.fingerprint === intentFingerprint) {
    return stored.id;
  }

  // Either nothing is stored, or what is stored belongs to a different intent
  // (the patient changed the slot, or a new patient is at the kiosk). Reusing
  // the id there would let the server replay someone else's booking.
  const created = globalThis.crypto.randomUUID();
  writeStoredSubmission(storageKey, {
    fingerprint: intentFingerprint,
    id: created,
  });
  return created;
}

/** What is remembered for a storage key: the id and the intent it belongs to. */
interface StoredSubmission {
  readonly fingerprint: string;
  readonly id: string;
}

/**
 * Fallback store for browsers that deny `sessionStorage`.
 *
 * Minting a fresh id per call there would send every retry under a new id and
 * create a duplicate — the exact failure this module exists to prevent — so the
 * id is held in memory for the page instead, disappearing with it.
 */
const inMemorySubmissions = new Map<string, StoredSubmission>();

function readStoredSubmission(
  storageKey: string,
): StoredSubmission | undefined {
  try {
    const raw = globalThis.sessionStorage.getItem(storageKey);
    if (raw === null || raw === "") return undefined;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "id" in parsed &&
      "fingerprint" in parsed &&
      typeof parsed.id === "string" &&
      typeof parsed.fingerprint === "string"
    ) {
      return { fingerprint: parsed.fingerprint, id: parsed.id };
    }
    return undefined;
  } catch {
    return inMemorySubmissions.get(storageKey);
  }
}

function writeStoredSubmission(
  storageKey: string,
  submission: StoredSubmission,
): void {
  try {
    globalThis.sessionStorage.setItem(storageKey, JSON.stringify(submission));
  } catch {
    inMemorySubmissions.set(storageKey, submission);
  }
}

/**
 * Forget the submission id for `storageKey`, so the next intent gets a new one.
 *
 * Call this once an action has reached a durable outcome the patient can see —
 * never while a retry is still possible, or the retry would create a duplicate.
 *
 * @since 1.1.0
 * @example
 * ```ts
 * clearClientSubmissionId("frontdesk-booking-submission-id")
 * ```
 */
export function clearClientSubmissionId(storageKey: string): void {
  inMemorySubmissions.delete(storageKey);
  try {
    globalThis.sessionStorage.removeItem(storageKey);
  } catch {
    // Storage denied: the in-memory id was already forgotten above, so the
    // next intent gets a new one either way.
  }
}
